import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cashReceipts, cashPayments } from '@/api/cashDocs';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { amountToWordsUA } from '@/utils/numberToWordsUA';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function dayMonthYear(iso: string): { d: number; m: string; y: number } {
  const dt = new Date(iso);
  return { d: dt.getDate(), m: MONTHS_UK[dt.getMonth()]!, y: dt.getFullYear() };
}

export default function CashOrderPrintPage({ kind }: { kind: 'receipt' | 'payment' }) {
  const { id } = useParams();
  const isReceipt = kind === 'receipt';

  const receiptQ = useQuery({
    queryKey: ['cash-receipt', id ?? ''],
    queryFn: () => cashReceipts.get(id!),
    enabled: !!id && isReceipt,
  });
  const paymentQ = useQuery({
    queryKey: ['cash-payment', id ?? ''],
    queryFn: () => cashPayments.get(id!),
    enabled: !!id && !isReceipt,
  });
  const docQ = isReceipt ? receiptQ : paymentQ;
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data || !orgsQ.data) {
    return <div className="card"><p>Завантаження…</p></div>;
  }

  const d = docQ.data;
  const org = orgsQ.data.items.find((o) => o.id === d.organization_id);
  const counterparty = d.contractor_id
    ? contractorsQ.data?.items.find((c) => c.id === d.contractor_id)
    : null;

  if (!org) return <div className="card"><p>Не знайдено організацію.</p></div>;

  const { d: day, m: month, y: year } = dayMonthYear(d.document_date);
  const personName = isReceipt
    ? (d as { received_from?: string | null }).received_from
      || counterparty?.full_name || counterparty?.name || '—'
    : (d as { issued_to?: string | null }).issued_to
      || counterparty?.full_name || counterparty?.name || '—';
  const titleRu = isReceipt
    ? 'ПРИБУТКОВИЙ КАСОВИЙ ОРДЕР'
    : 'ВИДАТКОВИЙ КАСОВИЙ ОРДЕР';

  return (
    <div className="print-page ko-print">
      <style>{`
        .ko-print {
          background: white; color: black; max-width: 760px; margin: 0 auto;
          padding: 1.5rem;
          font-family: 'Times New Roman', serif; font-size: 11pt; line-height: 1.4;
        }
        .ko-print h1 { font-size: 13pt; text-align: center; margin: 0; text-transform: uppercase; }
        .ko-print .top-row { display: grid; grid-template-columns: 1fr auto; gap: 1rem; }
        .ko-print .form-info { font-size: 9pt; color: #555; text-align: right; }
        .ko-print table.frame {
          width: 100%; border-collapse: collapse; margin-top: 0.75rem; font-size: 10pt;
        }
        .ko-print table.frame th, .ko-print table.frame td {
          border: 1px solid black; padding: 4px 6px; vertical-align: middle;
        }
        .ko-print table.frame th { background: #f5f5f5; font-weight: 600; text-align: center; }
        .ko-print .field-row { display: grid; grid-template-columns: 200px 1fr; gap: 0.5rem; margin: 0.4rem 0; }
        .ko-print .label { color: #555; }
        .ko-print .underlined { border-bottom: 1px solid black; padding: 0 4px; min-height: 1em; display: inline-block; min-width: 60%; }
        .ko-print .signs { margin-top: 1.5rem; }
        .ko-print .sign-row { display: grid; grid-template-columns: 200px 1fr 200px 1fr; gap: 0.5rem; margin: 0.5rem 0; }
        .ko-print .sign-line { border-bottom: 1px solid black; min-height: 18px; display: block; }
        .ko-print .sign-label { font-size: 9pt; color: #555; }
        .ko-print .actions { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
        @media print {
          /* Друк керує глобальний body.print-mode → .print-page (styles.css):
             ховає все, крiм цього бланка. Тут лише прибираемо тулбар і поля. */
          .ko-print { padding: 0; max-width: none; }
        }
      `}</style>

      <div className="actions print-hide">
        <button onClick={() => window.print()} className="primary">🖨 Друк / PDF</button>
        <button onClick={() => window.history.back()}>← Назад</button>
      </div>

      <div className="top-row">
        <div>
          <strong>{org.full_name || org.name}</strong>
          {org.edrpou && <div className="label" style={{ fontSize: '9pt' }}>ЄДРПОУ {org.edrpou}</div>}
        </div>
        <div className="form-info">
          Типова форма {isReceipt ? '№ КО-1' : '№ КО-2'}<br />
          Затверджено наказом Міністерства<br />
          фінансів України
        </div>
      </div>

      <h1 style={{ marginTop: '0.75rem' }}>{titleRu}</h1>
      <p style={{ textAlign: 'center', margin: '0.25rem 0' }}>
        № {d.document_number} від « {day} » {month} {year} р.
      </p>

      <table className="frame">
        <thead>
          <tr>
            <th rowSpan={2}>Дата складання</th>
            <th rowSpan={2}>Кореспондуючий рахунок,<br />субрахунок</th>
            <th rowSpan={2}>Код аналітичного<br />рахунку</th>
            <th rowSpan={2}>Сума, грн</th>
            <th rowSpan={2}>Код цільового<br />призначення</th>
          </tr>
          <tr></tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ textAlign: 'center' }}>{day}.{String(new Date(d.document_date).getMonth() + 1).padStart(2, '0')}.{year}</td>
            <td style={{ textAlign: 'center' }}>—</td>
            <td style={{ textAlign: 'center' }}>—</td>
            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt2(d.amount)}</td>
            <td>—</td>
          </tr>
        </tbody>
      </table>

      <div className="field-row" style={{ marginTop: '0.75rem' }}>
        <span className="label">{isReceipt ? 'Прийнято від:' : 'Видати:'}</span>
        <span className="underlined">{personName}</span>
      </div>

      <div className="field-row">
        <span className="label">Підстава:</span>
        <span className="underlined">{d.basis ?? '—'}</span>
      </div>

      <div className="field-row">
        <span className="label">Сума:</span>
        <span className="underlined"><em>{amountToWordsUA(d.amount)}</em></span>
      </div>

      <div className="field-row">
        <span className="label">Додаток:</span>
        <span className="underlined">{d.appendix ?? '—'}</span>
      </div>

      <div className="signs">
        <div className="sign-row">
          <span className="label">Керівник</span>
          <span className="sign-line">&nbsp;</span>
          <span className="label">Головний бухгалтер</span>
          <span className="sign-line">&nbsp;</span>
        </div>
        <div className="sign-row">
          <span className="sign-label">(підпис, П.І.Б.)</span>
          <span></span>
          <span className="sign-label">(підпис, П.І.Б.)</span>
          <span></span>
        </div>

        {!isReceipt && (
          <>
            <div className="sign-row" style={{ marginTop: '0.75rem' }}>
              <span className="label">Одержав:</span>
              <span className="sign-line">&nbsp;</span>
              <span></span>
              <span></span>
            </div>
            <div className="sign-row">
              <span className="sign-label">(сума прописом, підпис, дата)</span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="field-row">
              <span className="label">За документом:</span>
              <span className="underlined">&nbsp;</span>
            </div>
            <div className="sign-label" style={{ paddingLeft: '200px', marginTop: '-8px' }}>
              (найменування, номер, дата та місце видачі документа, що засвідчує особу одержувача)
            </div>
          </>
        )}

        <div className="sign-row" style={{ marginTop: '0.75rem' }}>
          <span className="label">Касир</span>
          <span className="sign-line">&nbsp;</span>
          <span></span>
          <span></span>
        </div>
        <div className="sign-row">
          <span className="sign-label">(підпис, П.І.Б.)</span>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  );
}
